import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  FileText, Upload, Search, Folder, Clock, User,
  PenTool, CheckCircle, XCircle, AlertCircle,
  RefreshCw, Download, Trash2, Edit, Eye, Plus,
  Filter, Tag, Calendar, FileSignature, Files
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentCenter = ({ patientId = null }) => {
  const [documents, setDocuments] = useState([]);
  const [pendingSignatures, setPendingSignatures] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('documents');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showUpload, setShowUpload] = useState(false);
  const [showSign, setShowSign] = useState(null);
  
  const [uploadForm, setUploadForm] = useState({
    patient_id: patientId || '',
    document_type: 'other',
    title: '',
    description: '',
    file_name: '',
    file_size: 0,
    file_type: '',
    file_path: '',
    tags: []
  });

  const [signatureForm, setSignatureForm] = useState({
    signature_type: 'typed',
    signature_value: ''
  });

  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    loadData();
  }, [patientId]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadDocuments(),
        loadPendingSignatures(),
        loadDocumentTypes(),
        loadTemplates(),
        loadStats()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const loadDocuments = async () => {
    try {
      let url = `${API_URL}/api/phase27/documents/search?query=`;
      if (patientId) {
        url = `${API_URL}/api/phase27/documents/patient/${patientId}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  };

  const loadPendingSignatures = async () => {
    try {
      // Use demo_user as default user_id for pending signatures
      const userId = 'demo_user';
      const response = await fetch(`${API_URL}/api/phase27/documents/signatures/pending?user_id=${userId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPendingSignatures(data.documents || []);
      }
    } catch (error) {
      console.error('Error loading pending signatures:', error);
    }
  };

  const loadDocumentTypes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase27/documents/types`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDocumentTypes(data.types || []);
      }
    } catch (error) {
      console.error('Error loading document types:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase27/documents/templates`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadStats = async () => {
    try {
      let url = `${API_URL}/api/phase27/documents/stats`;
      if (patientId) {
        url += `?patient_id=${patientId}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const uploadDocument = async () => {
    if (!uploadForm.title || !uploadForm.document_type) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/phase27/documents?uploaded_by=demo_user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          ...uploadForm,
          file_name: uploadForm.title + '.pdf',
          file_path: `/documents/${Date.now()}_${uploadForm.title}.pdf`
        })
      });

      if (response.ok) {
        toast.success('Document uploaded successfully');
        setShowUpload(false);
        loadDocuments();
        loadStats();
        setUploadForm({
          patient_id: patientId || '',
          document_type: 'other',
          title: '',
          description: '',
          file_name: '',
          file_size: 0,
          file_type: '',
          file_path: '',
          tags: []
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload document');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const deleteDocument = async (docId) => {
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      const response = await fetch(`${API_URL}/api/phase27/documents/${docId}?deleted_by=demo_user`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (response.ok) {
        toast.success('Document deleted');
        loadDocuments();
        loadStats();
      } else {
        toast.error('Failed to delete document');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const signDocument = async (docId, signatureId) => {
    if (!signatureForm.signature_value) {
      toast.error('Please enter your signature');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/phase27/documents/${docId}/sign/${signatureId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(signatureForm)
      });

      if (response.ok) {
        toast.success('Document signed successfully');
        setShowSign(null);
        loadPendingSignatures();
        loadDocuments();
        setSignatureForm({ signature_type: 'typed', signature_value: '' });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to sign document');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const searchDocuments = async () => {
    if (!searchQuery.trim()) {
      loadDocuments();
      return;
    }

    try {
      let url = `${API_URL}/api/phase27/documents/search?query=${encodeURIComponent(searchQuery)}`;
      if (filterType !== 'all') {
        url += `&document_type=${filterType}`;
      }
      if (patientId) {
        url += `&patient_id=${patientId}`;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      }
    } catch (error) {
      console.error('Error searching:', error);
    }
  };

  const getTypeBadge = (type) => {
    const colors = {
      consent: 'bg-blue-100 text-blue-800',
      advance_directive: 'bg-purple-100 text-purple-800',
      care_plan: 'bg-green-100 text-green-800',
      assessment: 'bg-yellow-100 text-yellow-800',
      medication_list: 'bg-orange-100 text-orange-800',
      physician_order: 'bg-red-100 text-red-800',
      face_to_face: 'bg-teal-100 text-teal-800',
      other: 'bg-gray-100 text-gray-800'
    };
    return (
      <Badge className={colors[type] || colors.other}>
        {type.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="document-loading">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="document-center">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Files className="h-7 w-7 text-teal-600" />
            Document Center
          </h1>
          <p className="text-gray-500">Manage documents and electronic signatures</p>
        </div>
        <Dialog open={showUpload} onOpenChange={setShowUpload}>
          <DialogTrigger asChild>
            <Button className="bg-teal-600 hover:bg-teal-700" data-testid="upload-document-btn">
              <Upload className="h-4 w-4 mr-2" />
              Upload Document
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Upload Document</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Document Title *</Label>
                <Input
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})}
                  placeholder="Enter document title"
                />
              </div>
              <div>
                <Label>Document Type *</Label>
                <Select
                  value={uploadForm.document_type}
                  onValueChange={(v) => setUploadForm({...uploadForm, document_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {documentTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Patient ID</Label>
                <Input
                  value={uploadForm.patient_id}
                  onChange={(e) => setUploadForm({...uploadForm, patient_id: e.target.value})}
                  placeholder="Enter patient ID (optional)"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                  placeholder="Document description..."
                />
              </div>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Drag & drop or click to upload</p>
                <p className="text-xs text-gray-400 mt-1">PDF, DOCX, JPG up to 10MB</p>
              </div>
              <Button className="w-full" onClick={uploadDocument}>
                Upload Document
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Documents</p>
                  <p className="text-2xl font-bold">{stats.total_documents}</p>
                </div>
                <FileText className="h-8 w-8 text-teal-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending Signatures</p>
                  <p className="text-2xl font-bold">{stats.pending_signatures}</p>
                </div>
                <PenTool className="h-8 w-8 text-yellow-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Expiring Soon</p>
                  <p className="text-2xl font-bold">{stats.expiring_soon}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Templates</p>
                  <p className="text-2xl font-bold">{templates.length}</p>
                </div>
                <Folder className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 flex gap-2">
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search documents..."
            onKeyPress={(e) => e.key === 'Enter' && searchDocuments()}
          />
          <Button onClick={searchDocuments}>
            <Search className="h-4 w-4" />
          </Button>
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {documentTypes.map((type) => (
              <SelectItem key={type.id} value={type.id}>
                {type.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="documents">All Documents</TabsTrigger>
          <TabsTrigger value="pending">
            Pending Signatures
            {pendingSignatures.length > 0 && (
              <Badge className="ml-2 bg-red-500">{pendingSignatures.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
        </TabsList>

        {/* Documents Tab */}
        <TabsContent value="documents" className="mt-4">
          {documents.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No documents found</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setShowUpload(true)}
                >
                  Upload First Document
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {documents.map((doc) => (
                <Card key={doc.id} data-testid={`document-${doc.id}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-gray-100 rounded-lg">
                          <FileText className="h-6 w-6 text-gray-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-medium">{doc.title}</h3>
                            {getTypeBadge(doc.document_type)}
                            {doc.requires_signature && (
                              <Badge variant="outline">
                                <PenTool className="h-3 w-3 mr-1" />
                                Requires Signature
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{doc.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-400">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(doc.uploaded_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              Version {doc.version}
                            </span>
                            {doc.tags?.length > 0 && (
                              <span className="flex items-center gap-1">
                                <Tag className="h-3 w-3" />
                                {doc.tags.join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-600 hover:text-red-700"
                          onClick={() => deleteDocument(doc.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Pending Signatures Tab */}
        <TabsContent value="pending" className="mt-4">
          {pendingSignatures.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle className="h-12 w-12 text-green-300 mx-auto mb-4" />
                <p className="text-gray-500">No pending signatures</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {pendingSignatures.map((doc) => (
                <Card key={doc.id} className="border-yellow-200 bg-yellow-50">
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-yellow-100 rounded-lg">
                          <FileSignature className="h-6 w-6 text-yellow-600" />
                        </div>
                        <div>
                          <h3 className="font-medium">{doc.title}</h3>
                          <p className="text-sm text-gray-600">{doc.description}</p>
                          <p className="text-sm text-gray-400 mt-1">
                            Uploaded: {formatDate(doc.uploaded_at)}
                          </p>
                        </div>
                      </div>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button className="bg-yellow-600 hover:bg-yellow-700">
                            <PenTool className="h-4 w-4 mr-2" />
                            Sign Now
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Sign Document</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div className="p-4 bg-gray-50 rounded-lg">
                              <h4 className="font-medium">{doc.title}</h4>
                              <p className="text-sm text-gray-500">{doc.description}</p>
                            </div>
                            <div>
                              <Label>Signature Type</Label>
                              <Select
                                value={signatureForm.signature_type}
                                onValueChange={(v) => setSignatureForm({...signatureForm, signature_type: v})}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="typed">Type Your Name</SelectItem>
                                  <SelectItem value="drawn">Draw Signature</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label>Your Signature</Label>
                              {signatureForm.signature_type === 'typed' ? (
                                <Input
                                  value={signatureForm.signature_value}
                                  onChange={(e) => setSignatureForm({...signatureForm, signature_value: e.target.value})}
                                  placeholder="Type your full legal name"
                                  className="font-serif text-xl"
                                />
                              ) : (
                                <div className="border rounded-lg h-32 flex items-center justify-center bg-white">
                                  <p className="text-gray-400">Drawing pad coming soon</p>
                                </div>
                              )}
                            </div>
                            <p className="text-xs text-gray-500">
                              By signing, you agree that this electronic signature is legally binding.
                            </p>
                            <Button 
                              className="w-full"
                              onClick={() => {
                                const pendingSig = doc.signatures?.find(s => s.status === 'pending');
                                if (pendingSig) {
                                  signDocument(doc.id, pendingSig.id);
                                }
                              }}
                            >
                              Sign Document
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => (
              <Card key={template.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-teal-100 rounded-lg">
                      <Folder className="h-5 w-5 text-teal-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium">{template.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                      <Badge variant="outline" className="mt-2">
                        {template.category}
                      </Badge>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="w-full mt-4">
                    Use Template
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DocumentCenter;
